export type DebugLogLevel = "debug" | "info" | "warn" | "error";

export interface DebugLogEntry {
  id: number;
  level: DebugLogLevel;
  category: string;
  message: string;
  details: string | null;
  createdAt: number;
  sessionElapsedMs: number;
  traceId: string | null;
  traceElapsedMs: number | null;
}

interface DebugLogInput {
  level: DebugLogLevel;
  category: string;
  message: string;
  details?: string | null;
  traceId?: string | null;
  traceElapsedMs?: number | null;
}

interface DebugLogStoreOptions {
  maxEntries?: number;
  now?: () => number;
  wallNow?: () => number;
}

const MAX_SERIALIZED_LENGTH = 8_000;

export function serializeDebugValue(value: unknown): string {
  const seen = new WeakSet<object>();

  let serialized: string;

  try {
    serialized =
      JSON.stringify(
        value,
        (_key, current: unknown) => {
          if (typeof current === "bigint") {
            return `${current}n`;
          }

          if (typeof current === "function") {
            return `[Function ${current.name || "anonymous"}]`;
          }

          if (current instanceof Error) {
            return {
              name: current.name,
              message: current.message,
              stack: current.stack,
            };
          }

          if (typeof Element !== "undefined" && current instanceof Element) {
            const id = current.id ? `#${current.id}` : "";
            return `<${current.tagName.toLowerCase()}${id}>`;
          }

          if (typeof current === "object" && current != null) {
            if (seen.has(current)) {
              return "[Circular]";
            }

            seen.add(current);
          }

          return current;
        },
        2,
      ) ?? String(value);
  } catch {
    try {
      serialized = String(value);
    } catch {
      serialized = "[Unserializable value]";
    }
  }

  if (serialized.length <= MAX_SERIALIZED_LENGTH) {
    return serialized;
  }

  return `${serialized.slice(0, MAX_SERIALIZED_LENGTH)}\n… truncated`;
}

export function createDebugLogStore(options: DebugLogStoreOptions = {}) {
  const maxEntries = Math.max(1, options.maxEntries ?? 500);
  const now = options.now ?? (() => performance.now());
  const wallNow = options.wallNow ?? Date.now;
  const sessionStartedAt = now();
  const listeners = new Set<() => void>();
  let entries: readonly DebugLogEntry[] = [];
  let nextId = 1;

  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  const append = (input: DebugLogInput): DebugLogEntry => {
    const entry: DebugLogEntry = {
      id: nextId++,
      level: input.level,
      category: input.category,
      message: input.message,
      details: input.details ?? null,
      createdAt: wallNow(),
      sessionElapsedMs: now() - sessionStartedAt,
      traceId: input.traceId ?? null,
      traceElapsedMs: input.traceElapsedMs ?? null,
    };

    entries = [...entries.slice(-(maxEntries - 1)), entry];
    notify();
    return entry;
  };

  const clear = () => {
    if (entries.length === 0) {
      return;
    }

    entries = [];
    notify();
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => void listeners.delete(listener);
  };

  return {
    append,
    clear,
    getSnapshot: () => entries,
    subscribe,
  };
}

export type DebugLogStore = ReturnType<typeof createDebugLogStore>;

export function formatDebugEntry(entry: DebugLogEntry): string {
  const time = new Date(entry.createdAt).toISOString().slice(11, 23);
  const trace =
    entry.traceId == null
      ? ""
      : ` [${entry.traceId} +${entry.traceElapsedMs?.toFixed(1) ?? "0.0"}ms]`;
  const header =
    `${time} +${entry.sessionElapsedMs.toFixed(1)}ms ` +
    `[${entry.level}] [${entry.category}]${trace} ${entry.message}`;
  return entry.details == null ? header : `${header}\n${entry.details}`;
}

export function formatDebugEntries(entries: readonly DebugLogEntry[]): string {
  return entries.map(formatDebugEntry).join("\n\n");
}

export const devDebugStore = createDebugLogStore();

const DEBUG_ENABLED = import.meta.env.DEV;
const VIDEO_TRACE_MAX_AGE_MS = 30_000;
let consoleCaptureInstalled = false;
let nextVideoTraceId = 1;

interface ActiveVideoTrace {
  id: string;
  action: string;
  startedAt: number;
}

const activeVideoTraces = new Map<string, ActiveVideoTrace>();

function monotonicNow(): number {
  return performance.now();
}

function appendDebugLog(
  level: DebugLogLevel,
  category: string,
  message: string,
  data?: unknown,
  trace?: ActiveVideoTrace,
): void {
  if (!DEBUG_ENABLED) {
    return;
  }

  devDebugStore.append({
    level,
    category,
    message,
    details: data === undefined ? null : serializeDebugValue(data),
    traceId: trace?.id ?? null,
    traceElapsedMs: trace == null ? null : monotonicNow() - trace.startedAt,
  });
}

export function devDebugLog(
  category: string,
  message: string,
  data?: unknown,
  level: DebugLogLevel = "debug",
): void {
  appendDebugLog(level, category, message, data);
}

function getActiveVideoTrace(videoId: string): ActiveVideoTrace | null {
  const trace = activeVideoTraces.get(videoId);

  if (trace == null) {
    return null;
  }

  if (monotonicNow() - trace.startedAt <= VIDEO_TRACE_MAX_AGE_MS) {
    return trace;
  }

  activeVideoTraces.delete(videoId);
  return null;
}

export function startVideoTiming(
  videoId: string,
  action: string,
  data?: unknown,
): string | null {
  if (!DEBUG_ENABLED) {
    return null;
  }

  const trace: ActiveVideoTrace = {
    id: `video-${nextVideoTraceId++}`,
    action,
    startedAt: monotonicNow(),
  };
  activeVideoTraces.set(videoId, trace);
  appendDebugLog(
    "info",
    "video",
    action,
    { videoId, ...asRecord(data) },
    trace,
  );
  return trace.id;
}

export function ensureVideoTiming(
  videoId: string,
  action: string,
  data?: unknown,
): string | null {
  const active = getActiveVideoTrace(videoId);

  if (active != null) {
    return active.id;
  }

  return startVideoTiming(videoId, action, data);
}

export function isVideoTimingTraceActive(
  videoId: string,
  traceId: string,
): boolean {
  return getActiveVideoTrace(videoId)?.id === traceId;
}

export function logVideoTiming(
  videoId: string,
  event: string,
  data?: unknown,
  options?: {
    expectedTraceId?: string;
    finish?: boolean;
    level?: DebugLogLevel;
  },
): void {
  if (!DEBUG_ENABLED) {
    return;
  }

  const activeTrace = getActiveVideoTrace(videoId) ?? undefined;

  if (
    options?.expectedTraceId != null &&
    activeTrace?.id !== options.expectedTraceId
  ) {
    return;
  }

  const trace = activeTrace;
  appendDebugLog(
    options?.level ?? "debug",
    "video",
    event,
    { videoId, action: trace?.action, ...asRecord(data) },
    trace,
  );

  if (options?.finish && trace != null) {
    activeVideoTraces.delete(videoId);
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value != null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : value === undefined
      ? {}
      : { value };
}

function consoleMessage(args: unknown[]): {
  message: string;
  details: unknown | undefined;
} {
  if (typeof args[0] === "string") {
    return {
      message: args[0],
      details:
        args.length <= 1
          ? undefined
          : args.length === 2
            ? args[1]
            : args.slice(1),
    };
  }

  return {
    message:
      args.length === 1 ? serializeDebugValue(args[0]) : "Console output",
    details: args.length <= 1 ? undefined : args,
  };
}

export function installDevConsoleCapture(): void {
  if (!DEBUG_ENABLED || consoleCaptureInstalled) {
    return;
  }

  consoleCaptureInstalled = true;

  type ConsoleMethod = DebugLogLevel | "log";
  const consoleMethods: Array<{
    method: ConsoleMethod;
    level: DebugLogLevel;
  }> = [
    { method: "debug", level: "debug" },
    { method: "log", level: "info" },
    { method: "info", level: "info" },
    { method: "warn", level: "warn" },
    { method: "error", level: "error" },
  ];
  const mutableConsole = console as unknown as Record<
    ConsoleMethod,
    (...args: unknown[]) => void
  >;

  for (const { method, level } of consoleMethods) {
    const original = mutableConsole[method].bind(console);

    try {
      mutableConsole[method] = (...args: unknown[]) => {
        const { message, details } = consoleMessage(args);
        appendDebugLog(level, "console", message, details);
        original(...args);
      };
    } catch {
      // Some embedded runtimes expose non-writable console methods. The
      // dedicated debug logger and the remaining methods still work.
    }
  }

  window.addEventListener("error", (event) => {
    appendDebugLog(
      "error",
      "window",
      event.message || "Unhandled window error",
      event.error ?? {
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      },
    );
  });
  window.addEventListener("unhandledrejection", (event) => {
    appendDebugLog(
      "error",
      "window",
      "Unhandled promise rejection",
      event.reason,
    );
  });
}
